import { Inject } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils'
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ClickEvent, FetchEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ActionAPI, AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeSubmit, OnChange, OnClick, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';

import moment from 'moment';
import DialogService from '../../../../../services/dialog-service';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { SearchType } from '../../../../health-history/health-history-types';
import { WizardSteps } from '../../../../../constants/wizards';
import { DateFilterUtils } from '../../../../../../src/utils/date-filter-utils';
import { LocatorTypes } from '../../../../locator/locator-controller';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { WoundsDressingsTabListContext } from '../../../service/wounds-dressing-tab-list-service';
import { DressingsCreateAliases, FragmentNames } from '../../../../../codegen/index';
import { DomainType, DressingsResponse, HoursMinutesType, WoundsDressingTabType } from '../../../types/wounds-dressings-types';

@Controller({ component: DressingsCreateAliases.LAYOUT_DRESSINGS_CREATE, fragment: FragmentNames.DRESSINGS_CREATE })
export default class DressingsIncludesController extends BaseController {

  @Inject(DressingsCreateAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_SECTOR)
  private autocompleteDressingSectorAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_SIZE)
  private autocompleteDressingSizeAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_DEPTH)
  private autocompleteDressingDepthAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_PRIORITY)
  private autocompleteDressingPriorityAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_DEAD_SPACE)
  private autocompleteDressingDeadSpaceAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_TYPE_NECROTIC_TISSUE)
  private autocompleteDressingTypeNecroticAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_AMOUNT_NECROTIC_TISSUE)
  private autocompleteDressingAmountNecroticTissueAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_TYPE_OF_EXUDATE)
  private autocompleteDressingTypeOfExudateAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_EXUDATE_AMOUNT)
  private autocompleteDressingExudateAmountAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_SKIN_COLOR_AROUND_WOUND)
  private autocompleteDressingColorAroundWoundAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_PERIPHERAL_TISSUE_EDEMA)
  private autocompleteDressingPeripheralTissueEdemaAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_HARDENING_OF_PERIPHERAL_TISSUE)
  private autocompleteDressingHardeningOfPeripheralTissueAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_GRANULATION_TISSUE)
  private autocompleteDressingGranulationTissueAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_EPITHELIALIZATION)
  private autocompleteDressingEpithelializationAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_PAIN_AROUND)
  private autocompleteDressingPainAroundAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_GANGRENE)
  private autocompleteDressingGangreneAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_MACERATION)
  private autocompleteDressingMacerationAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.INPUT_DRESSING_TOTAL_SCORE)
  private inputDressingTotalScore!: InputAPI;

  @Inject(DressingsCreateAliases.INPUT_DRESSING_VALIDITY_HOURS)
  private inputDressingValidityHours!: InputAPI;

  @Inject(DressingsCreateAliases.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FORM_FOUR)
  private formFourAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FORM_FIVE)
  private formFiveAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FORM_SIX)
  private formFormSixAPI!: FormAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_RECORD_DATE)
  private fieldRecordDateApi!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_RECORD_TIME)
  private fieldRecordTimeApi!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_SIZE)
  private fieldDressingSize!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_DEPTH)
  private fieldDressingDepth!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_PRIORITY)
  private fieldDressingPriority!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_DEAD_SPACE)
  private fieldDressingDeadSpace!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_TYPE_NECROTIC_TISSUE)
  private fieldDressingTypeNecroticTissue!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_AMOUNT_NECROTIC_TISSUE)
  private fieldDressingAmountNecroticTissue!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_TYPE_OF_EXUDATE)
  private fieldDressingTypeOfExudate!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_EXUDATE_AMOUNT)
  private fieldDressingExudateAmount!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_SKIN_COLOR_AROUND_WOUND)
  private fieldDressingSkinColorAroundWound!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_PERIPHERAL_TISSUE_EDEMA)
  private fieldDressingPeripheralTissueEdema!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_HARDENING_OF_PERIPHERAL_TISSUE)
  private fieldDressingHardeningPeripheralTissue!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_GRANULATION_TISSUE)
  private fieldDressingGranulationTissue!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_EPITHELIALIZATION)
  private fieldDressingEpithelialization!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_PAIN_AROUND)
  private fieldDressingPainAround!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_GANGRENE)
  private fieldDressingGangrene!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_MACERATION)
  private fieldDressingMaceration!: FieldAPI;

  @Inject(DressingsCreateAliases.FIELD_DRESSING_DEGREE_OR_STAGE)
  private fieldDressingDegreeOrStage!: FieldAPI;

  @Inject(DressingsCreateAliases.AUTOCOMPLETE_DRESSING_DEGREE_OR_STAGE)
  private autocompleteDressingDegreeOrStageAPI!: AutocompleteAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_SIZE)
  private fetchDressingSizeApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_DEPTH)
  private fetchDressingDepthApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_PRIORITY)
  private fetchDressingPriorityApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_DEAD_SPACE)
  private fetchDressingDeadSpaceApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_TYPE_NECROTIC_TISSUE)
  private fetchDressingTypeNecroticApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_AMOUNT_NECROTIC_TISSUE)
  private fetchDressingAmountNecroticTissueApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_TYPE_OF_EXUDATE)
  private fetchDressingTypeOfExudateApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_EXUDATE_AMOUNT)
  private fetchDressingExudateAmountApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_SKIN_COLOR_AROUND_WOUND)
  private fetchDressingColorAroundWoundApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_PERIPHERAL_TISSUE_EDEMA)
  private fetchDressingPeripheralTissueEdemaApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_HARDENING_OF_PERIPHERAL_TISSUE)
  private fetchDressingHardeningOfPeripheralTissueApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_GRANULATION_TISSUE)
  private fetchDressingGranulationTissueApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_EPITHELIALIZATION)
  private fetchDressingEpithelializationApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_PAIN_AROUND)
  private fetchDressingPainAroundApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_GANGRENE)
  private fetchDressingGangreneApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_MACERATION)
  private fetchDressingMacerationApi!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_DEGREE_OR_STAGE)
  private fetchDressingDegreeOrStage!: FetchAPI;

  @Inject(DressingsCreateAliases.FETCH_DRESSING_SECTOR)
  private fetchDressingSector!: FetchAPI;

  @Inject(DressingsCreateAliases.ACTION_CANCEL_DRESSING)
  private actionCancelDressingApi!: ActionAPI;

  @Inject(DressingsCreateAliases.ACTION_RELEASE_DRESSING)
  private actionReleaseDressingApi!: ActionAPI;

  @OnMount(DressingsCreateAliases.LAYOUT_DRESSINGS_CREATE)
  handleLayoutMount(): void {
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const establishmentId = foundationContext.getEstablishment()?.id;
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, establishmentId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
  }

  private forceFetchAllAutocompleteFields(): void {
    this.fetchDressingSizeApi.forceFetch()
    this.fetchDressingDepthApi.forceFetch()
    this.fetchDressingPriorityApi.forceFetch()
    this.fetchDressingDeadSpaceApi.forceFetch()
    this.fetchDressingTypeNecroticApi.forceFetch()
    this.fetchDressingAmountNecroticTissueApi.forceFetch()
    this.fetchDressingTypeOfExudateApi.forceFetch()
    this.fetchDressingExudateAmountApi.forceFetch()
    this.fetchDressingColorAroundWoundApi.forceFetch()
    this.fetchDressingPeripheralTissueEdemaApi.forceFetch()
    this.fetchDressingHardeningOfPeripheralTissueApi.forceFetch()
    this.fetchDressingGranulationTissueApi.forceFetch()
    this.fetchDressingEpithelializationApi.forceFetch()
    this.fetchDressingPainAroundApi.forceFetch()
    this.fetchDressingGangreneApi.forceFetch()
    this.fetchDressingMacerationApi.forceFetch()
    this.fetchDressingDegreeOrStage.forceFetch()
    this.fetchDressingSector.forceFetch()
  }

  private getDressingDetail(dressingId: number | string): Promise<DressingsResponse | void> {
    return HttpService.get(Endpoint.DRESSING, { dressingId })
      .then(response => {
        const dressing = response.body as DressingsResponse;
        this.context.set({ dressing });
      }).catch((error) => this.showToast(error?.body.errorMessage, ToastLevel.ERROR))
  }

  @OnMount(DressingsCreateAliases.FORM_ONE)
  handleMountFormOne({ target }: EventData<FormAPI>): void {
    target.setLoading(true)
    if (this.isEditingDressing()) {
      const dressingId = this.historyService.getCurrentRouteQueryParams().dressingId as unknown as string | number;
      this.getDressingDetail(dressingId).finally(() => {
        target.setLoading(false)
        this.loadDataIntoForms()
        this.validateVisibilityActions()
        this.forceFetchAllAutocompleteFields()
      });
      return
    }
    target.setData({
      dateRecord: this.currentDate(),
      timeRecord: this.currentTime()
    });
    target.setLoading(false)
  }

  @OnUnmount(DressingsCreateAliases.LAYOUT_DRESSINGS_CREATE)
  handleUnmountLayout(): void {
    WoundsDressingsTabListContext.clean();
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
  }

  private validateVisibilityActions(): void {
    const dressing = this.context.get('dressing') as DressingsResponse
    if (!dressing?.releaseDate) {
      this.actionReleaseDressingApi.setEnabled(true)
      this.actionCancelDressingApi.setEnabled(true)
    }
  }

  private loadDataIntoForms(): void {
    const dressing = this.context.get('dressing') as DressingsResponse
    this.formOneAPI.setData({
      dateRecord: moment(dressing.recordDate).utc().format(ISO_DATE_FORMAT),
      timeRecord: moment(String(dressing.recordDate)).utc().format(HoursMinutesType.ISO_HOURS_MINUTES_FORMAT)
    })
    this.formFiveAPI.setData({
      depthCm: !isNaN(Number(dressing.depthCm)) ? Number(dressing.depthCm) : null ,
      lenghtCm: !isNaN(Number(dressing.lenghtCm)) ? Number(dressing.lenghtCm) : null,
      resultDsScore: dressing.resultDsScore as string,
      timeMinutes: !isNaN(Number(dressing.timeMinutes)) ? Number(dressing.timeMinutes) : null,
      totalScore: Number(dressing.totalScore),
      widthCm: !isNaN(Number(dressing.widthCm)) ? Number(dressing.widthCm) : null
    })
    this.formFormSixAPI.setData({
      notes: dressing.notes as string,
      result: dressing.result as string,
      validityHours: Number(dressing.validityHours)
    })
  }

  @OnMount([DressingsCreateAliases.FORM_TWO, DressingsCreateAliases.FORM_THREE, DressingsCreateAliases.FORM_FOUR])
  handleMountFormTwo(): void {
    if(!this.isEditingDressing()) {
      this.forceFetchAllAutocompleteFields()
    }
  }

  @OnChange(DressingsCreateAliases.FIELD_DRESSING_RECORD_DATE)
  handleChangeCpoeFilterDate({ value }: ClickEvent): void {
    const date = moment(value);
    if (value.length == HoursMinutesType.TEEN_VALUE && moment(date).isValid()) {
      this.fieldRecordDateApi.setData({ dateRecord: moment(date).utc().format(ISO_DATE_FORMAT) });
    }
  }

  @OnChange(DressingsCreateAliases.FIELD_DRESSING_RECORD_TIME)
  handleChangeCpoeFilterHour(event: ClickEvent): void {
    this.fieldRecordTimeApi.setData({ timeRecord: event.value });
  }

  private isEditingDressing(): boolean {
    const dressingId = this.historyService.getCurrentRouteQueryParams().dressingId as unknown as string | number;
    return dressingId != 'null' && dressingId != undefined
  }

  private currentTime(): string {
    return DateFilterUtils.getCurrentTimeUtc();
  }

  private currentDate(): string {
    return DateFilterUtils.getCurrentDateUtc();
  }

  @OnAfterNavigate(DressingsCreateAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_FOUR, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_FIVE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_SIX, true);
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    this.wizardAPI.setStepDone(step, isStepDone);
  }

  @OnValidate(DressingsCreateAliases.FORM_ONE)
  handleOnValidate(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(DressingsCreateAliases.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(DressingsCreateAliases.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnValidate(DressingsCreateAliases.FORM_FOUR)
  handleValidateFormFour(validationFormFour: ValidateEvent): void {
    this.handleStepDone(validationFormFour.valid, WizardSteps.STEP_FOUR);
  }

  @OnValidate(DressingsCreateAliases.FORM_FIVE)
  handleValidateFormFive(validationFormFive: ValidateEvent): void {
    this.handleStepDone(validationFormFive.valid, WizardSteps.STEP_FIVE);
  }

  @OnValidate(DressingsCreateAliases.FORM_SIX)
  handleValidateFormSix(validationFormSix: ValidateEvent): void {
    this.handleStepDone(validationFormSix.valid, WizardSteps.STEP_SIX);
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_SECTOR)
  handleFetchSector(event: FetchEvent): void {
    const dressing = this.context.getBulkData().dressing as DressingsResponse;
    const defaultValue = foundationContext.getSector()?.id;
    const infoSector = dressing != undefined ? dressing.sector : defaultValue;
    const sectors = (event.data as Record<string, Value>[]);
    const defaultSelected = sectors.find(item => item.id === infoSector);
    this.autocompleteDressingSectorAPI.setValue(defaultSelected);
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_SIZE)
  handleFetchDressingSize(event: FetchEvent): void {
    const entry = this.fieldDressingSize.getEntry();
    this.autocompleteDressingSizeAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_DEPTH)
  handleFetchDressingDepth(event: FetchEvent): void {
    const entry = this.fieldDressingDepth.getEntry();
    this.autocompleteDressingDepthAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_PRIORITY)
  handleFetchDressingPriority(event: FetchEvent): void {
    const entry = this.fieldDressingPriority.getEntry();
    this.autocompleteDressingPriorityAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_DEAD_SPACE)
  handleFetchDressingDeadSpace(event: FetchEvent): void {
    const entry = this.fieldDressingDeadSpace.getEntry();
    this.autocompleteDressingDeadSpaceAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_TYPE_NECROTIC_TISSUE)
  handleFetchDressingTypeNecroticTissue(event: FetchEvent): void {
    const entry = this.fieldDressingTypeNecroticTissue.getEntry();
    this.autocompleteDressingTypeNecroticAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_AMOUNT_NECROTIC_TISSUE)
  handleFetchDressingAmountNecroticTissue(event: FetchEvent): void {
    const entry = this.fieldDressingAmountNecroticTissue.getEntry();
    this.autocompleteDressingAmountNecroticTissueAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_TYPE_OF_EXUDATE)
  handleFetchDressingTypeOfExudate(event: FetchEvent): void {
    const entry = this.fieldDressingTypeOfExudate.getEntry();
    this.autocompleteDressingTypeOfExudateAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_EXUDATE_AMOUNT)
  handleFetchDressingExudateAmount(event: FetchEvent): void {
    const entry = this.fieldDressingExudateAmount.getEntry();
    this.autocompleteDressingExudateAmountAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_SKIN_COLOR_AROUND_WOUND)
  handleFetchDressingColorAroundWound(event: FetchEvent): void {
    const entry = this.fieldDressingSkinColorAroundWound.getEntry();
    this.autocompleteDressingColorAroundWoundAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_PERIPHERAL_TISSUE_EDEMA)
  handleFetchDressingPeripheralTissueEdema(event: FetchEvent): void {
    const entry = this.fieldDressingPeripheralTissueEdema.getEntry();
    this.autocompleteDressingPeripheralTissueEdemaAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_HARDENING_OF_PERIPHERAL_TISSUE)
  handleFetchDressingHardeningOfPeripheralTissue(event: FetchEvent): void {
    const entry = this.fieldDressingHardeningPeripheralTissue.getEntry();
    this.autocompleteDressingHardeningOfPeripheralTissueAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_GRANULATION_TISSUE)
  handleFetchDressingGranulationTissue(event: FetchEvent): void {
    const entry = this.fieldDressingGranulationTissue.getEntry();
    this.autocompleteDressingGranulationTissueAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_EPITHELIALIZATION)
  handleFetchDressingEpithelialization(event: FetchEvent): void {
    const entry = this.fieldDressingEpithelialization.getEntry();
    this.autocompleteDressingEpithelializationAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_PAIN_AROUND)
  handleFetchDressingPainAround(event: FetchEvent): void {
    const entry = this.fieldDressingPainAround.getEntry();
    this.autocompleteDressingPainAroundAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_GANGRENE)
  handleFetchDressingGangrene(event: FetchEvent): void {
    const entry = this.fieldDressingGangrene.getEntry();
    this.autocompleteDressingGangreneAPI.setValue(this.defaultSelected(event, entry));
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_MACERATION)
  handleFetchDressingMaceration(event: FetchEvent): void {
    const entry = this.fieldDressingMaceration.getEntry();
    this.autocompleteDressingMacerationAPI.setValue(this.defaultSelected(event, entry));
  }

  private defaultSelected(event: FetchEvent, entry: string): Record<string, Value> | undefined {
    let defaultValue = '1';
    const dressing = this.context.getBulkData().dressing as DressingsResponse;
    if(dressing != undefined){
      if(entry in dressing){
        defaultValue = dressing[entry as keyof typeof dressing] as string;
      }
    }
    const fetchEvent = event.data as Record<string, Value>[];
    return fetchEvent.find(item => item.valueDomain === String(defaultValue));
  }

  @OnMount(DressingsCreateAliases.FIELD_DRESSING_TOTAL_SCORE)
  handleOnMountFieldTotalScore(): void {
    const defaultTotalScore = 0;
    this.inputDressingTotalScore.setValue(defaultTotalScore);
  }

  @OnMount(DressingsCreateAliases.FIELD_DRESSING_VALIDITY_HOURS)
  handleOnMountFieldValidityHours(): void {
    const defaultValidityHours = 12;
    this.inputDressingValidityHours.setValue(defaultValidityHours);
  }

  @OnAfterFetch(DressingsCreateAliases.FETCH_DRESSING_DEGREE_OR_STAGE)
  handleOnMountDressingDegreeOrStage(event: FetchEvent): void {
    const dressing = this.context.getBulkData().dressing as DressingsResponse;
    const isDressingId = dressing != undefined ? true : false;
    if(isDressingId && dressing.degreeOrStage != undefined){
      const entry = this.fieldDressingDegreeOrStage.getEntry();
      this.autocompleteDressingDegreeOrStageAPI.setValue(this.defaultSelected(event, entry));
    }
  }

  @OnClick(DressingsCreateAliases.ACTION_RELEASE_DRESSING)
  handleClickRelease(): void {
    const { dressingId } = this.context.getBulkData().dressing as DressingsResponse;
    this.showModalSubmit(dressingId as number);
  }

  @OnClick(DressingsCreateAliases.ACTION_CANCEL_DRESSING)
  handleClickCancel(): void {
    const { dressingId } = this.context.getBulkData().dressing as DressingsResponse;
    this.confirmDelete(dressingId as number);
  }

  private confirmDelete(dressingId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => {  this.historyService.back(); },
      onClickOk: () => { this.delete(dressingId)},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private delete(dressingId: number): void {
    HttpService.delete(Endpoint.DRESSING, { dressingId })
      .then(() => {
        this.historyService.back();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      })
  }

  private handleCreateDressing(body: Record<string, Value>): void {
    this.formFourAPI.setLoading(true);

    HttpService.post(Endpoint.POST_DRESSING, undefined, { body })
      .then((res) => {
        const { dressingId } = res.body as DressingsResponse;
        this.showModalSubmit(dressingId as number);
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formFourAPI.setLoading(false));
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'dressings-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
  }

  private showModalSubmit(dressingId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack(); },
      onClickOk: () => { this.handleReleaseDressing(dressingId) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private handleReleaseDressing(dressingId: number): void {
    HttpService.put(Endpoint.RELEASE_DRESSING, { dressingId })
      .then(() => {
        applicationContext.popNavigationItem();
        WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      });
  }

  @OnBeforeSubmit(DressingsCreateAliases.WIZARD)
  handleBeforeSubmit(): void {
    const { encounterId, woundId } = this.historyService.getCurrentRouteParams();
    const formDataOne = this.formOneAPI.getRecord();
    const formDataTwo = this.formTwoAPI.getRecord();
    const formDataThree = this.formThreeAPI.getRecord();
    const formDataFour = this.formFourAPI.getRecord();
    const formDataFive = this.formFiveAPI.getRecord();
    const formDataSix = this.formFormSixAPI.getRecord();
    const valueFieldHour = formDataOne.timeRecord
    const valueFieldDate =this.fieldRecordDateApi.getValue() as string
    const recordDate = moment(`${valueFieldDate}T${valueFieldHour}`).utc().format(ISO_FULL_DATE_FORMAT);
    const dressingId = this.historyService.getCurrentRouteQueryParams().dressingId;
    const degreeOrStage = Number((formDataSix.degreeOrStage as DomainType)?.valueDomain);

    const body = {
      amountNecroticTissue: Number((formDataThree.amountNecroticTissue as DomainType)?.valueDomain),
      deadSpace: Number((formDataTwo.deadSpace as DomainType)?.valueDomain),
      degreeOrStage: !isNaN(degreeOrStage) ? degreeOrStage : null ,
      depth: Number((formDataTwo.depth as DomainType)?.valueDomain),
      depthCm: !isNaN(Number(formDataFive.depthCm)) ?  Math.trunc(Number(formDataFive.depthCm) * 100) / 100 : null,
      dressingId,
      dressingSize: Number((formDataTwo.dressingSize as DomainType)?.valueDomain),
      encounterId: parseInt(encounterId as unknown as string),
      epithelialization: Number((formDataFour.epithelialization as DomainType)?.valueDomain),
      exudateAmount: Number((formDataThree.exudateAmount as DomainType)?.valueDomain),
      gangrene: Number((formDataFour.gangrene as DomainType)?.valueDomain),
      granulationTissue: Number((formDataFour.granulationTissue as DomainType)?.valueDomain),
      hardeningPeripheralTissue: Number((formDataFour.hardeningPeripheralTissue as DomainType)?.valueDomain),
      lenghtCm: !isNaN(Number(formDataFive.lenghtCm)) ? Math.trunc(Number(formDataFive.lenghtCm) * 100) / 100 : null,
      maceration: Number((formDataFour.maceration as DomainType)?.valueDomain),
      notes: formDataSix.notes != undefined ? formDataSix.notes : '' ,
      painAround: Number((formDataFour.painAround as DomainType)?.valueDomain),
      peripheralTissueEdema: Number((formDataThree.peripheralTissueEdema as DomainType)?.valueDomain),
      priorityBorder: Number((formDataTwo.priorityBorder as DomainType)?.valueDomain),
      recordDate,
      sector: (formDataOne.sector as SearchType)?.id,
      skinColorAroundWound: Number((formDataThree.skinColorAroundWound as DomainType)?.valueDomain),
      timeMinutes: !isNaN(Number(formDataFive.timeMinutes)) ? Number(formDataFive.timeMinutes) : null,
      totalScore: Number(formDataFive.totalScore),
      typeExudate: Number((formDataThree.typeExudate as DomainType)?.valueDomain),
      typeNecroticTissue: Number((formDataTwo.typeNecroticTissue as DomainType)?.valueDomain),
      validityHours: !isNaN(Number(formDataSix.validityHours)) ? Number(formDataSix.validityHours) : null,
      widthCm: !isNaN(Number(formDataFive.widthCm)) ? Math.trunc(Number(formDataFive.widthCm) * 100) / 100 : null,
      woundId: Number(woundId)
    } as Record<string, Value>

    this.handleCreateDressing(body);
  }
}
